// WYVIO UI 1.0 — Fiche Dossier V3
// Route canonique : /app/dossier/:id  ·  Alias : /app/dossier-v3/:id
// Cockpit central. Identité anthracite / ivoire / lime. Aucun changement API/métier.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation, NavLink } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, CalendarClock, MessageCircle,
  FileText, Settings, HelpCircle, ArrowRight, ChevronDown,
  ArrowLeft, Check, Clock, AlertCircle, Send, Paperclip, MoreHorizontal,
  Wallet, Store,
} from "lucide-react";

import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppSidebar } from "@/components/AppSidebar";
import { useUnread } from "@/context/UnreadContext";
import { SilhouetteVehicle } from "@/components/wyvio-ui/signature/SilhouetteVehicle";

/* ═════════════════════════ Utils métier ═════════════════════ */
const STEP_LABELS = {
  reservation: "Réservation",
  acompte:     "Acompte",
  admin:       "Documents",
  check:       "Check-list",
  ct:          "Contrôle technique",
  esthetique:  "Esthétique",
  pret:        "Prêt à livrer",
  livraison:   "Livraison",
  cpi:         "PV de livraison",
  paiement:    "Paiement",
};

const guessSilhouette = (name = "") => {
  const v = name.toLowerCase();
  if (/gt3|coupe|coupé|911|m2|m4|corvette|amg gt|f-type|cayman/.test(v)) return "coupe";
  if (/suv|q3|q5|q7|q8|x1|x3|x5|x7|macan|cayenne|glc|gle|gls|3008|5008|2008|rav|kodiaq/.test(v)) return "suv";
  if (/rs6|break|estate|touring|allroad|shooting|avant/.test(v)) return "break";
  return "berline";
};

const fmtDate = (iso, opts = { day: "numeric", month: "long", year: "numeric" }) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", opts); } catch { return "—"; }
};

const relFromDelivery = (iso) => {
  if (!iso) return null;
  const days = Math.round((new Date(iso).getTime() - Date.now()) / 86400000);
  if (days === 0) return "aujourd'hui";
  if (days === 1) return "demain";
  if (days > 0)   return `dans ${days} jours`;
  return `il y a ${-days} jours`;
};

/* ═════════════════════════ HeroDossier ═════════════════════ */
const HeroDossier = ({ dossier }) => {
  const silhouette = guessSilhouette(dossier?.vehicle_name);
  const statusColor = dossier?.current_status?.color;
  const statusMap = {
    green:  { dot: "#C8FF2F", txt: "text-black" },
    blue:   { dot: "#3B82F6", txt: "text-black" },
    orange: { dot: "#F59E0B", txt: "text-black" },
    red:    { dot: "#EF4444", txt: "text-black" },
    gray:   { dot: "#94A3B8", txt: "text-black" },
    purple: { dot: "#8B5CF6", txt: "text-black" },
  };
  const s = statusMap[statusColor] || statusMap.gray;

  return (
    <section
      className="relative mt-6 overflow-hidden rounded-3xl"
      style={{ background: "linear-gradient(180deg, #0C0C0C 0%, #0A0A0A 100%)" }}
      data-testid="dossier-v3-hero"
    >
      <div className="grid grid-cols-1 md:grid-cols-[1.05fr_1fr]">
        {/* Colonne texte */}
        <div className="flex flex-col justify-between p-10 md:p-12">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/40">
                {dossier?.order_number || "—"}
              </span>
              <span className="h-1 w-1 rounded-full bg-white/25" />
              <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/40">
                {dossier?.workflow_slug === "suivi_de_vente" ? "Suivi de vente" : (dossier?.workflow_slug || "workflow")}
              </span>
            </div>

            <h1 className="mt-4 text-[44px] font-semibold leading-[1.02] tracking-[-0.025em] text-white">
              {dossier?.vehicle_name || "Véhicule"}
            </h1>

            <p className="mt-2 text-[15px] text-white/50">
              {dossier?.client?.name || dossier?.client_name || "Client"}
            </p>

            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/[0.06] pl-2 pr-3.5 py-1.5"
                 style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)" }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.dot, boxShadow: `0 0 8px ${s.dot}66` }} />
              <span className="text-[11.5px] font-medium text-white/85">
                {dossier?.current_status?.label || "En cours"}
              </span>
            </div>
          </div>

          {/* Progression */}
          <div className="mt-10">
            <div className="mb-2 flex items-baseline justify-between">
              <span className="text-[10.5px] uppercase tracking-[0.16em] text-white/40">Avancement dossier</span>
              <span className="text-[14px] font-medium text-white/90">{dossier?.progress || 0}%</span>
            </div>
            <div className="relative h-[6px] w-full overflow-hidden rounded-full bg-white/[0.08]">
              <div className="absolute inset-y-0 left-0 rounded-full"
                   style={{ width: `${dossier?.progress || 0}%`, background: "linear-gradient(90deg, #EAEAEA 0%, #FFFFFF 100%)" }} />
              {(dossier?.progress || 0) > 4 && (
                <div className="absolute top-0 h-full w-[6px] rounded-full"
                     style={{ left: `calc(${dossier?.progress || 0}% - 6px)`, background: "#C8FF2F", boxShadow: "0 0 6px rgba(200,255,47,0.7)" }} />
              )}
            </div>
          </div>
        </div>

        {/* Colonne véhicule */}
        <div className="relative min-h-[320px]">
          {dossier?.vehicle_image ? (
            <img src={dossier.vehicle_image} alt={dossier.vehicle_name}
                 className="absolute inset-0 h-full w-full object-cover"
                 onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <SilhouetteVehicle type={silhouette} variant="dark" className="w-[68%]" />
            </div>
          )}
          {/* Gradient de fondu vers la gauche pour lisibilité */}
          <div className="absolute inset-y-0 left-0 w-[40%]"
               style={{ background: "linear-gradient(90deg, rgba(10,10,10,0.65) 0%, rgba(10,10,10,0) 100%)" }} />
        </div>
      </div>
    </section>
  );
};

/* ═════════════════════════ Meta (4 stats minimales) ═══════ */
const DossierMeta = ({ dossier }) => {
  const items = [
    { label: "Réservé le",    value: fmtDate(dossier?.reservation_date, { day: "numeric", month: "long" }) },
    { label: "Disponibilité", value: fmtDate(dossier?.availability_date, { day: "numeric", month: "long" }) },
    { label: "Livraison",     value: fmtDate(dossier?.delivery_date, { day: "numeric", month: "long" }), rel: relFromDelivery(dossier?.delivery_date) },
    { label: "Commercial",    value: dossier?.commercial_name || "Non assigné" },
  ];
  return (
    <section className="mt-10 grid grid-cols-2 gap-x-10 gap-y-6 sm:grid-cols-4" data-testid="dossier-v3-meta">
      {items.map((it) => (
        <div key={it.label}>
          <div className="text-[10px] uppercase tracking-[0.16em] text-black/40">{it.label}</div>
          <div className="mt-1.5 text-[15px] font-medium text-black">{it.value}</div>
          {it.rel && <div className="mt-0.5 text-[11.5px] text-black/45">{it.rel}</div>}
        </div>
      ))}
    </section>
  );
};

/* ═════════════════════════ Next Action Panel ══════════════ */
const NextActionPanel = ({ dossier }) => {
  const next = dossier?.workflow_summary?.next_action;
  const label = next?.label || STEP_LABELS[next?.key] || "Aucune action";
  const consequence = next?.blocking_reason || "Toutes les étapes préalables sont complètes.";
  const why = "Cette étape débloque la mise à disposition du véhicule pour le client.";

  return (
    <section
      className="relative mt-10 overflow-hidden rounded-3xl bg-white p-10"
      style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.02), inset 0 0 0 1px rgba(0,0,0,0.05)" }}
      data-testid="dossier-v3-next-action"
    >
      {/* Halo lime discret coin haut-droit */}
      <div aria-hidden className="pointer-events-none absolute right-[-80px] top-[-80px] h-[220px] w-[220px] rounded-full"
           style={{ background: "radial-gradient(circle, rgba(200,255,47,0.12) 0%, rgba(200,255,47,0) 70%)" }} />

      <div className="relative flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
        <div className="max-w-[600px]">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-black/40">Prochaine action</div>
          <h2 className="mt-3 text-[36px] font-semibold leading-[1.05] tracking-[-0.02em] text-black">
            {label}
          </h2>

          <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-black/40">Pourquoi</div>
              <p className="mt-2 text-[13.5px] leading-relaxed text-black/70">{why}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-black/40">Conséquence</div>
              <p className="mt-2 text-[13.5px] leading-relaxed text-black/70">{consequence}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-3">
          <button className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-[13px] font-medium text-black/70 transition-colors hover:text-black"
                  style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}
                  data-testid="dossier-v3-secondary">
            Déléguer
          </button>
          <button className="inline-flex items-center gap-2 rounded-full bg-[#C8FF2F] px-5 py-2.5 text-[13px] font-medium text-black transition-transform hover:scale-[1.01] active:scale-[0.98]"
                  style={{ boxShadow: "0 6px 16px -8px rgba(200,255,47,0.55), 0 1px 2px rgba(0,0,0,0.04)" }}
                  data-testid="dossier-v3-primary">
            Passer à l&apos;action <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ═════════════════════════ Payment Panel (agence) ═════════
   Rend visible l'action paiement transversale, indépendante de la timeline.
   4 cas distincts alignés sur la règle métier :
   - confirmed              → chip discret "Paiement confirmé"
   - status=declared        → "Vérifier le paiement" (le client a déclaré)
   - will_settle_in_agency  → "Règlement prévu en agence" + CTA confirmer
   - reminder_due (pending) → "Rappeler le paiement" + CTA confirmer + toggle "en agence"
   Sinon: null (contrôles pas terminés → paiement pas encore actionnable). */
const PaymentPanel = ({ dossier, busy, onSettleInAgency, onConfirm }) => {
  const ps = dossier?.payment_summary || {};
  const amountLabel = (n) => (n == null ? null : new Intl.NumberFormat("fr-FR", {
    style: "currency", currency: ps.currency || "EUR", maximumFractionDigits: 0,
  }).format(n));

  // Cas confirmed — chip discret, pas de panneau bloquant
  if (ps.status === "confirmed") {
    const amt = amountLabel(ps.amount_paid);
    return (
      <section className="mt-6" data-testid="dossier-v3-payment">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#F1FBCB] px-3 py-1.5"
             style={{ boxShadow: "inset 0 0 0 1px rgba(200,255,47,0.35)" }}>
          <Check size={13} className="text-[#4A6300]" strokeWidth={2.5} />
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-[#4A6300]">
            Paiement confirmé
          </span>
          {amt && <span className="text-[11.5px] font-mono text-[#4A6300]/70">{amt}</span>}
        </div>
      </section>
    );
  }

  // Rien à afficher tant que les contrôles ne sont pas OK (paiement non pertinent)
  if (!ps.reminder_due && !ps.will_settle_in_agency && ps.status !== "declared") {
    return null;
  }

  // Cas declared — le client a déclaré, à vérifier par l'agence
  if (ps.status === "declared") {
    return (
      <section className="mt-6" data-testid="dossier-v3-payment">
        <div className="relative flex flex-col gap-4 rounded-2xl bg-white p-6 md:flex-row md:items-center md:justify-between"
             style={{ boxShadow: "inset 0 0 0 1px rgba(139,92,246,0.25), 0 1px 2px rgba(0,0,0,0.02)" }}>
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-[#ECE4FE] px-2 py-0.5">
              <span className="text-[9.5px] font-semibold uppercase tracking-[0.1em] text-[#5B21B6]">
                À vérifier
              </span>
            </div>
            <h3 className="mt-2 text-[18px] font-semibold text-black">Vérifier le paiement</h3>
            <p className="mt-1 text-[13px] text-black/60">
              Le client a déclaré avoir réglé {amountLabel(ps.declared_amount) || "le solde"}
              {ps.declared_method ? ` par ${ps.methods_catalog?.[ps.declared_method] || ps.declared_method}` : ""}.
              Confirmez la réception pour finaliser.
            </p>
          </div>
          <button onClick={onConfirm} disabled={busy}
                  className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-[#C8FF2F] px-5 py-2.5 text-[13px] font-medium text-black transition-transform hover:scale-[1.01] active:scale-[0.98]"
                  style={{ boxShadow: "0 6px 16px -8px rgba(200,255,47,0.55)" }}
                  data-testid="dossier-v3-payment-confirm">
            {busy ? "…" : (<>Confirmer le paiement <ArrowRight size={13} /></>)}
          </button>
        </div>
      </section>
    );
  }

  // Cas will_settle_in_agency (indépendant de reminder_due, prioritaire à l'écran)
  if (ps.will_settle_in_agency) {
    return (
      <section className="mt-6" data-testid="dossier-v3-payment">
        <div className="relative flex flex-col gap-4 rounded-2xl bg-white p-6 md:flex-row md:items-center md:justify-between"
             style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.02)" }}>
          <div className="min-w-0 flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-black/[0.05]">
              <Store size={16} className="text-black/60" strokeWidth={1.7} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.14em] text-black/45">Paiement</div>
              <h3 className="mt-0.5 text-[17px] font-semibold text-black">Règlement prévu en agence</h3>
              <p className="mt-0.5 text-[12.5px] text-black/55">
                Le client réglera au comptoir lors de la livraison. Confirmez le paiement en un clic le jour J.
              </p>
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <button onClick={() => onSettleInAgency?.(false)} disabled={busy}
                    className="rounded-full bg-white px-3 py-2 text-[12px] font-medium text-black/60 transition-colors hover:text-black"
                    style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }}>
              Annuler
            </button>
            <button onClick={onConfirm} disabled={busy}
                    className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-[12.5px] font-medium text-white transition-transform hover:scale-[1.01] active:scale-[0.98]"
                    data-testid="dossier-v3-payment-confirm">
              {busy ? "…" : (<>Confirmer paiement <ArrowRight size={13} /></>)}
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Cas reminder_due (pending, contrôles OK, pas d'intention agence)
  return (
    <section className="mt-6" data-testid="dossier-v3-payment">
      <div className="relative flex flex-col gap-4 rounded-2xl bg-white p-6 md:flex-row md:items-center md:justify-between"
           style={{ boxShadow: "inset 0 0 0 1px rgba(200,255,47,0.40), 0 1px 2px rgba(0,0,0,0.02)" }}>
        <div className="min-w-0 flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#F1FBCB]">
            <Wallet size={16} className="text-[#4A6300]" strokeWidth={1.9} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.14em] text-black/45">Paiement</div>
            <h3 className="mt-0.5 text-[17px] font-semibold text-black">Rappeler le paiement</h3>
            <p className="mt-0.5 text-[12.5px] text-black/55">
              Les contrôles sont terminés — le solde doit être réglé avant la livraison.
              {ps.reminders_count > 0 && <span className="ml-1 text-black/40">· {ps.reminders_count} rappel{ps.reminders_count > 1 ? "s" : ""} envoyé{ps.reminders_count > 1 ? "s" : ""}</span>}
            </p>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <button onClick={() => onSettleInAgency?.(true)} disabled={busy}
                  className="rounded-full bg-white px-3 py-2 text-[12px] font-medium text-black/60 transition-colors hover:text-black"
                  style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }}
                  data-testid="dossier-v3-payment-settle-agency">
            Règlement en agence
          </button>
          <button onClick={onConfirm} disabled={busy}
                  className="inline-flex items-center gap-2 rounded-full bg-[#C8FF2F] px-4 py-2 text-[12.5px] font-medium text-black transition-transform hover:scale-[1.01] active:scale-[0.98]"
                  style={{ boxShadow: "0 6px 16px -8px rgba(200,255,47,0.55)" }}
                  data-testid="dossier-v3-payment-confirm">
            {busy ? "…" : (<>Confirmer paiement <ArrowRight size={13} /></>)}
          </button>
        </div>
      </div>
    </section>
  );
};

/* ═════════════════════════ CockpitTimeline ═══════════════ */
const CockpitTimeline = ({ dossier }) => {
  const rawSteps = dossier?.steps || [];
  const activeKey = dossier?.workflow_summary?.active_step?.key;
  const totals = dossier?.workflow_summary?.totals || {};
  const completedCount = totals?.completed || 0;

  // Utilise STRICTEMENT le libellé métier renvoyé par le backend.
  // Fallback : `title` (parfois utilisé) → clé technique brute.
  const steps = rawSteps.map((s, i) => ({
    key: s.key,
    label: s.label || s.title || s.key,
    order: s.order ?? i,
    state: (i < completedCount)
      ? "done"
      : (s.key === activeKey)
        ? "active"
        : "pending",
  }));

  return (
    <section className="mt-10" data-testid="dossier-v3-timeline">
      <div className="mb-5 flex items-baseline justify-between">
        <span className="text-[10.5px] uppercase tracking-[0.2em] text-black">Cockpit</span>
        <span className="text-[11px] uppercase tracking-[0.18em] text-black/40">
          {completedCount}/{steps.length} étapes complétées
        </span>
      </div>

      <div className="rounded-3xl bg-white/60 p-8"
           style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.02), inset 0 0 0 1px rgba(0,0,0,0.04)" }}>
        {/* Rail horizontal — scroll x sur mobile */}
        <div className="relative flex items-start gap-2 overflow-x-auto pb-1">
          {steps.map((st, i) => {
            const isDone = st.state === "done";
            const isActive = st.state === "active";
            const dotStyle = isDone
              ? { bg: "#0C0C0C", ring: "#0C0C0C", inner: <Check size={11} className="text-white" strokeWidth={3} /> }
              : isActive
                ? { bg: "#C8FF2F", ring: "#C8FF2F", inner: <span className="h-1.5 w-1.5 rounded-full bg-black" /> }
                : { bg: "#FFFFFF",  ring: "rgba(0,0,0,0.14)", inner: null };
            return (
              <div key={st.key + i} className="flex min-w-[110px] flex-1 flex-col items-center">
                <div className="flex w-full items-center">
                  <div className={["h-[2px] flex-1 rounded-full", i === 0 ? "opacity-0" : ""].join(" ")}
                       style={{ background: isDone || isActive ? "#0C0C0C" : "rgba(0,0,0,0.08)" }} />
                  <div className="mx-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
                       style={{ background: dotStyle.bg, boxShadow: `inset 0 0 0 1.5px ${dotStyle.ring}` }}>
                    {dotStyle.inner}
                  </div>
                  <div className={["h-[2px] flex-1 rounded-full", i === steps.length - 1 ? "opacity-0" : ""].join(" ")}
                       style={{ background: isDone ? "#0C0C0C" : "rgba(0,0,0,0.08)" }} />
                </div>
                <div className={[
                  "mt-3 max-w-[100px] text-center text-[11px] leading-tight",
                  isActive ? "font-semibold text-black" : isDone ? "text-black/75" : "text-black/40",
                ].join(" ")}>
                  {st.label}
                </div>
                {isActive && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.14em] text-black/50">
                    <Clock size={9} /> En cours
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ═════════════════════════ Documents card ═════════════════ */
const DocumentsCard = ({ dossier }) => {
  const docs = dossier?.documents || [];
  const displayDocs = docs.length > 0 ? docs.slice(0, 4) : [
    { id: "d1", name: "Bon de commande signé",  date_label: "il y a 3 j",   status: "signed" },
    { id: "d2", name: "Carte grise",             date_label: "il y a 2 j",   status: "available" },
    { id: "d3", name: "Attestation assurance",   date_label: "en attente",   status: "pending" },
    { id: "d4", name: "PV livraison",            date_label: "—",            status: "upcoming" },
  ];

  // Nom lisible : name → type → filename → fallback court
  const humanName = (d) =>
    d.name || d.display_name || d.type_label || d.type || d.filename || "Pièce sans nom";

  // Mapping statut → visuel WYVIO (lime = validé/positif, violet = à vérifier, gris = en attente)
  const STATE = {
    signed:    { txt: "Signé",       color: "#4A6300", bg: "#F1FBCB" },
    validated: { txt: "Validé",      color: "#4A6300", bg: "#F1FBCB" },
    available: { txt: "Disponible",  color: "#4A6300", bg: "#F1FBCB" },
    pending:   { txt: "À vérifier",  color: "#5B21B6", bg: "#ECE4FE" },
    upcoming:  { txt: "En attente",  color: "#374151", bg: "#EEF0F2" },
    waiting:   { txt: "En attente",  color: "#374151", bg: "#EEF0F2" },
  };

  return (
    <div className="rounded-2xl bg-white/60 p-6"
         style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.02), inset 0 0 0 1px rgba(0,0,0,0.04)" }}
         data-testid="dossier-v3-documents">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-black">Documents</div>
          <div className="mt-1 text-[11.5px] text-black/45">{docs.length || displayDocs.length} pièces au dossier</div>
        </div>
        <button className="text-[11.5px] font-medium text-black/60 transition-colors hover:text-black">
          Tout ouvrir →
        </button>
      </div>

      <ul className="mt-5 space-y-1.5">
        {displayDocs.map((d) => {
          const st = STATE[d.status] || STATE.upcoming;
          return (
            <li key={d.id} className="flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-black/[0.03]">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-black/[0.05]">
                <FileText size={14} className="text-black/55" strokeWidth={1.7} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium text-black">{humanName(d)}</div>
                <div className="text-[11px] text-black/40">{d.date_label || "—"}</div>
              </div>
              <span className="rounded-full px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.08em]"
                    style={{ background: st.bg, color: st.color }}>
                {st.txt}
              </span>
            </li>
          );
        })}
      </ul>

      <button className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-medium text-black/60 hover:text-black">
        <Paperclip size={12} /> Ajouter une pièce
      </button>
    </div>
  );
};

/* ═════════════════════════ Utils dates humaines ═══════════ */
const humanTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  const yesterday = d.toDateString() === y.toDateString();
  const hhmm = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  if (sameDay)   return `Aujourd'hui · ${hhmm}`;
  if (yesterday) return `Hier · ${hhmm}`;
  const days = Math.round((now.getTime() - d.getTime()) / 86400000);
  if (days > 0 && days < 7) return `Il y a ${days} j`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) + ` · ${hhmm}`;
};

/* ═════════════════════════ Messages card (contextualisé) ═ */
const MessagesCard = ({ dossier, onSent }) => {
  const msgs = dossier?.messages || [];
  const hasReal = msgs.length > 0;
  const list = hasReal ? msgs.slice(-3) : [
    { id: "m1", from_side: "agency", author: "Stéphane", text: "Bonjour Marie, votre Porsche 911 est prête. Quand souhaitez-vous la récupérer ?", created_at: new Date(Date.now() - 86400000).toISOString() },
    { id: "m2", from_side: "client", author: "Marie",    text: "Parfait ! Vendredi 15h serait idéal.",                                              created_at: new Date().toISOString() },
  ];

  // Sprint P0.1 — index du premier message client non lu dans la SLICE affichée
  //   pour insérer un séparateur "Nouveaux" au-dessus.
  const firstUnreadIdxInList = hasReal
    ? list.findIndex((m) => m.from_side === "client" && !m.read_by_agency_at)
    : -1;
  const unreadCount = dossier?.messages_summary?.unread_count || 0;

  const clientFirstName = (dossier?.client?.name || "").split(/\s+/)[0] || "client";
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const rootRef = useRef(null);
  const markedRef = useRef(false);
  const unread = useUnread();
  const location = useLocation();

  // Deep-link #messages → scroll auto
  useEffect(() => {
    if (location.hash !== "#messages" || !rootRef.current) return;
    // Petit délai pour que le layout soit stable
    const t = setTimeout(() => {
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
    return () => clearTimeout(t);
  }, [location.hash, dossier?.id]);

  // Mark-read par visibilité — seuil 60% pendant 800ms avant de déclencher.
  useEffect(() => {
    markedRef.current = false;
  }, [dossier?.id]);

  useEffect(() => {
    if (!hasReal || unreadCount === 0 || !rootRef.current || markedRef.current) return;
    const el = rootRef.current;
    let timer = null;
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e.isIntersecting && e.intersectionRatio >= 0.6) {
        if (!timer) {
          timer = setTimeout(async () => {
            if (markedRef.current) return;
            markedRef.current = true;
            try {
              await api.markAgencyMessagesRead(dossier.id);
              if (onSent) await onSent();          // rafraîchit le dossier (met à jour messages_summary + separator)
              if (unread?.refresh) unread.refresh(); // met à jour badge sidebar
            } catch {
              markedRef.current = false;           // en cas d'erreur, autorise un retry
            }
          }, 800);
        }
      } else if (timer) {
        clearTimeout(timer); timer = null;
      }
    }, { threshold: [0, 0.6, 1] });
    io.observe(el);
    return () => { if (timer) clearTimeout(timer); io.disconnect(); };
  }, [hasReal, unreadCount, dossier?.id, onSent, unread]);

  const send = async () => {
    const value = text.trim();
    if (!value || sending || !dossier?.id) return;
    setSending(true);
    setErr(null);
    try {
      await api.sendAgencyMessage(dossier.id, value);
      setText("");
      if (onSent) await onSent();
      if (unread?.refresh) unread.refresh(); // refresh immédiat après envoi
    } catch (e) {
      setErr(e?.data?.detail || e?.message || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div
      ref={rootRef}
      id="messages"
      className="rounded-2xl bg-white/60 p-6"
      style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.02), inset 0 0 0 1px rgba(0,0,0,0.04)" }}
      data-testid="dossier-v3-messages"
    >
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-black">Messages</div>
          <div className="mt-1 text-[11.5px] text-black/45">Fil dossier · {list.length} récents</div>
        </div>
        <button
          type="button"
          onClick={() => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
          className="text-[11.5px] font-medium text-black/60 transition-colors hover:text-black"
          data-testid="dossier-v3-messages-open"
        >
          Ouvrir la conversation →
        </button>
      </div>

      <ul className="mt-5 space-y-4">
        {list.map((m, idx) => {
          const isAgency = m.from_side === "agency";
          const when = humanTime(m.created_at);
          const showSep = idx === firstUnreadIdxInList && unreadCount > 0;
          return (
            <div key={m.id}>
              {showSep && (
                <li className="flex items-center gap-3 py-1" data-testid="dossier-v3-messages-separator">
                  <span className="h-px flex-1" style={{ background: "rgba(200,255,47,0.35)" }} />
                  <span className="text-[9.5px] uppercase tracking-[0.18em] text-black/45">
                    Nouveau{unreadCount > 1 ? "x" : ""}
                  </span>
                  <span className="h-px flex-1" style={{ background: "rgba(200,255,47,0.35)" }} />
                </li>
              )}
              <li className={["flex flex-col gap-1", isAgency ? "items-end" : "items-start"].join(" ")}>
                <div className="flex items-baseline gap-2 text-[10px] uppercase tracking-[0.14em] text-black/40">
                  <span>{m.author}</span>{when && <><span>·</span><span>{when}</span></>}
                </div>
                <div className={[
                  "max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed",
                  isAgency ? "text-white" : "bg-white text-black/85",
                ].join(" ")}
                     style={isAgency
                       ? { background: "#171717", boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)" }
                       : { boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }}>
                  {m.text}
                </div>
              </li>
            </div>
          );
        })}
      </ul>

      {/* Input contextualisé — branché sur POST /api/dossiers/:id/messages */}
      <div className="mt-6 flex items-center gap-2 rounded-full bg-white px-3 py-2"
           style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          disabled={sending}
          placeholder={`Répondre à ${clientFirstName}…`}
          className="flex-1 bg-transparent text-[13px] text-black placeholder:text-black/35 focus:outline-none disabled:opacity-60"
          data-testid="dossier-v3-message-input"
        />
        <button
          type="button"
          onClick={send}
          disabled={sending || !text.trim()}
          aria-label="Envoyer"
          className="flex h-8 w-8 items-center justify-center rounded-full bg-[#C8FF2F] text-black transition-transform hover:scale-[1.03] disabled:opacity-50 disabled:hover:scale-100"
          data-testid="dossier-v3-message-send"
        >
          <Send size={13} strokeWidth={2} />
        </button>
      </div>
      {err && (
        <div className="mt-2 text-[11.5px] text-red-600" data-testid="dossier-v3-message-error">
          {err}
        </div>
      )}
    </div>
  );
};

/* ═════════════════════════ Skel ═════════════════════════ */
const Skel = () => (
  <div className="mt-6 h-[360px] w-full animate-pulse rounded-3xl bg-black/[0.04]" />
);

/* ═════════════════════════ MAIN ═════════════════════════ */
export default function DossierV3() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { user, agency } = useAuth();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentBusy, setPaymentBusy] = useState(false);

  const reload = async (id) => {
    const d = await api.getDossier(id);
    setDossier(d);
  };

  const handleSettleInAgency = async (value) => {
    if (!dossier?.id || paymentBusy) return;
    setPaymentBusy(true);
    try {
      await api.settlePaymentInAgency(dossier.id, value);
      await reload(dossier.id);
    } catch (e) {
      console.error("settle-in-agency failed", e);
    } finally {
      setPaymentBusy(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!dossier?.id || paymentBusy) return;
    if (!window.confirm("Confirmer le paiement pour ce dossier ?")) return;
    setPaymentBusy(true);
    try {
      await api.confirmPayment(dossier.id, {});
      await reload(dossier.id);
    } catch (e) {
      console.error("confirm-payment failed", e);
    } finally {
      setPaymentBusy(false);
    }
  };

  // Fallback : si aucun id fourni, on prend le premier dossier avec image dispo
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        let id = routeId;
        if (!id) {
          const list = await api.listDossiers();
          const items = Array.isArray(list) ? list : (list?.items || []);
          const target = items.find((x) => x.vehicle_image && !x.is_finished && x.progress >= 40) || items[0];
          id = target?.id;
        }
        if (!id) throw new Error("Aucun dossier disponible");
        const d = await api.getDossier(id);
        if (!cancel) setDossier(d);
      } catch (e) {
        if (!cancel) setError(e?.message || "Erreur de chargement");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [routeId]);

  return (
    <div className="relative min-h-screen text-black"
         style={{ background: "radial-gradient(1000px 600px at 100% 0%, rgba(200,255,47,0.04) 0%, rgba(200,255,47,0) 45%), linear-gradient(180deg, #FAFAF7 0%, #F5F4EF 100%)" }}
         data-testid="dossier-v3-page">
      <AppSidebar agency={agency} user={user} testId="dossier-v3-sidebar" />

      <main className="lg:pl-[260px]">
        <div className="mx-auto max-w-[1120px] px-10 py-12">
          {/* Top nav row : retour + breadcrumb + actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-[12.5px] font-medium text-black/60 transition-colors hover:text-black"
              data-testid="dossier-v3-back"
            >
              <ArrowLeft size={14} /> Retour
            </button>
            <div className="flex items-center gap-2">
              <button className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[11.5px] font-medium text-black/60"
                      style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }}>
                <AlertCircle size={12} /> Signaler
              </button>
              <button className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-black/50"
                      style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }}>
                <MoreHorizontal size={14} />
              </button>
            </div>
          </div>

          {loading && <Skel />}
          {error && !loading && (
            <div className="mt-10 rounded-2xl bg-white/60 p-10 text-center text-[13.5px] text-black/50"
                 style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}>
              {error}
            </div>
          )}

          {!loading && dossier && (
            <>
              <HeroDossier  dossier={dossier} />
              <DossierMeta  dossier={dossier} />
              <NextActionPanel dossier={dossier} />
              <PaymentPanel
                dossier={dossier}
                busy={paymentBusy}
                onConfirm={handleConfirmPayment}
                onSettleInAgency={handleSettleInAgency}
              />
              <CockpitTimeline dossier={dossier} />

              {/* Documents + Messages en 2 colonnes minimalistes */}
              <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="dossier-v3-bottom">
                <DocumentsCard dossier={dossier} />
                <MessagesCard  dossier={dossier} onSent={() => reload(dossier.id)} />
              </section>
            </>
          )}

          <div className="h-24" />
        </div>
      </main>
    </div>
  );
}
